import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar } from "recharts";

const money = (v) => "₹" + Number(v || 0).toLocaleString("en-IN");

export default function Reports() {
  const { data: dash } = useQuery({ queryKey: ["dashboard-reports"], queryFn: () => api.get("/reports/dashboard").then((r) => r.data) });
  const { data: perf } = useQuery({ queryKey: ["driver-perf"], queryFn: () => api.get("/reports/driver-performance").then((r) => r.data) });
  const { data: gst } = useQuery({ queryKey: ["gst"], queryFn: () => api.get("/accounts/gst-report").then((r) => r.data) });
  const { data: sales } = useQuery({ queryKey: ["sales-report"], queryFn: () => api.get("/reports/sales").then((r) => r.data) });

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="font-display text-3xl font-black tracking-tight">Reports & Analytics</h1>
        <p className="text-sm text-muted-foreground">Sales, stock, shortage, drivers, GST — all in one place.</p>
      </div>

      <Tabs defaultValue="sales">
        <TabsList>
          <TabsTrigger value="sales" data-testid="tab-sales">Sales</TabsTrigger>
          <TabsTrigger value="stock" data-testid="tab-stock">Stock</TabsTrigger>
          <TabsTrigger value="sale-report" data-testid="tab-sale-report">Sale (bowser/driver)</TabsTrigger>
          <TabsTrigger value="shortage" data-testid="tab-shortage">Shortage</TabsTrigger>
          <TabsTrigger value="driver" data-testid="tab-driver">Drivers</TabsTrigger>
          <TabsTrigger value="gst" data-testid="tab-gst">GST</TabsTrigger>
        </TabsList>

        <TabsContent value="sales" className="mt-4 space-y-4">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <KPI label="Invoices" value={sales?.count || 0} />
            <KPI label="Litres" value={`${sales?.total_quantity || 0} L`} />
            <KPI label="Revenue" value={`₹${(sales?.total_revenue || 0).toLocaleString("en-IN")}`} highlight />
            <KPI label="Avg / invoice" value={`₹${sales?.count ? Math.round(sales.total_revenue / sales.count).toLocaleString("en-IN") : 0}`} />
          </div>
          <Card>
            <CardHeader><CardTitle>Revenue trend</CardTitle></CardHeader>
            <CardContent className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dash?.series || []}>
                  <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
                  <XAxis dataKey="date" fontSize={11} tickFormatter={(v) => v.slice(5)} />
                  <YAxis fontSize={11} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
                  <Bar dataKey="revenue" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="stock" className="mt-4"><StockReport /></TabsContent>
        <TabsContent value="sale-report" className="mt-4"><SaleReport /></TabsContent>
        <TabsContent value="shortage" className="mt-4"><ShortageReport /></TabsContent>

        <TabsContent value="driver" className="mt-4">
          <Card>
            <CardHeader><CardTitle>Driver performance</CardTitle></CardHeader>
            <CardContent className="p-0">
              <table className="data-table w-full">
                <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
                  <tr><th className="px-4 py-3">Code</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Phone</th><th className="px-4 py-3 text-right">Trips</th><th className="px-4 py-3 text-right">Litres delivered</th></tr>
                </thead>
                <tbody>
                  {(perf?.items || []).map((r) => (
                    <tr key={r.driver_code} className="border-b border-border/40 text-sm">
                      <td className="px-4 py-3 font-mono font-semibold">{r.driver_code}</td>
                      <td className="px-4 py-3">{r.name}</td>
                      <td className="px-4 py-3 text-xs">{r.phone}</td>
                      <td className="px-4 py-3 text-right font-mono">{r.trips}</td>
                      <td className="px-4 py-3 text-right font-mono">{r.litres_delivered.toLocaleString("en-IN")}L</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="gst" className="mt-4 space-y-4">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <KPI label="Invoices" value={gst?.count || 0} />
            <KPI label="Taxable value" value={`₹${(gst?.total_subtotal || 0).toLocaleString("en-IN")}`} />
            <KPI label="CGST + SGST" value={`₹${((gst?.total_cgst || 0) + (gst?.total_sgst || 0)).toLocaleString("en-IN")}`} />
            <KPI label="Grand total" value={`₹${(gst?.total || 0).toLocaleString("en-IN")}`} highlight />
          </div>
          <Card>
            <CardContent className="p-0">
              <table className="data-table w-full">
                <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
                  <tr><th className="px-4 py-3">Invoice</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Customer</th><th className="px-4 py-3 text-right">Taxable</th><th className="px-4 py-3 text-right">CGST</th><th className="px-4 py-3 text-right">SGST</th><th className="px-4 py-3 text-right">Total</th></tr>
                </thead>
                <tbody>
                  {(gst?.invoices || []).map((i) => (
                    <tr key={i.id} className="border-b border-border/40 text-sm">
                      <td className="px-4 py-3 font-mono">{i.code}</td>
                      <td className="px-4 py-3 text-xs">{new Date(i.invoice_date).toLocaleDateString()}</td>
                      <td className="px-4 py-3">{i.customer_name}</td>
                      <td className="px-4 py-3 text-right font-mono">₹{i.subtotal.toLocaleString("en-IN")}</td>
                      <td className="px-4 py-3 text-right font-mono">₹{i.cgst.toLocaleString("en-IN")}</td>
                      <td className="px-4 py-3 text-right font-mono">₹{i.sgst.toLocaleString("en-IN")}</td>
                      <td className="px-4 py-3 text-right font-mono font-bold">₹{i.total.toLocaleString("en-IN")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

const KPI = ({ label, value, highlight }) => (
  <div className="kpi-card">
    <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className={`mt-2 font-display text-2xl font-black ${highlight ? "text-primary" : ""}`}>{value}</div>
  </div>
);

function StockReport() {
  const { data } = useQuery({ queryKey: ["stock-report"], queryFn: () => api.get("/reports/stock-report").then((r) => r.data) });
  return (
    <Card>
      <CardHeader><CardTitle>Bowser-wise stock & shortage</CardTitle></CardHeader>
      <CardContent className="p-0">
        <table className="data-table w-full">
          <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="px-4 py-2">Bowser</th>
              <th className="px-4 py-2">Registration</th>
              <th className="px-4 py-2 text-right">Capacity</th>
              <th className="px-4 py-2 text-right">Loaded (L)</th>
              <th className="px-4 py-2 text-right">Delivered (L)</th>
              <th className="px-4 py-2 text-right">Self-fuel (L)</th>
              <th className="px-4 py-2 text-right">Current</th>
              <th className="px-4 py-2 text-right">Expected</th>
              <th className="px-4 py-2 text-right">Shortage</th>
            </tr>
          </thead>
          <tbody>
            {(data?.rows || []).map((r) => (
              <tr key={r.bowser_id} className="border-b border-border/40 text-sm" data-testid={`stock-row-${r.bowser_code}`}>
                <td className="px-4 py-2 font-mono">{r.bowser_code}</td>
                <td className="px-4 py-2 font-mono text-xs">{r.registration}</td>
                <td className="px-4 py-2 text-right font-mono">{r.capacity}</td>
                <td className="px-4 py-2 text-right font-mono">{r.loaded_litres.toLocaleString("en-IN")}</td>
                <td className="px-4 py-2 text-right font-mono">{r.delivered_litres.toLocaleString("en-IN")}</td>
                <td className="px-4 py-2 text-right font-mono">{r.self_fuel_litres.toLocaleString("en-IN")}</td>
                <td className="px-4 py-2 text-right font-mono">{r.current_stock.toLocaleString("en-IN")}</td>
                <td className="px-4 py-2 text-right font-mono">{r.expected_stock.toLocaleString("en-IN")}</td>
                <td className={`px-4 py-2 text-right font-mono font-bold ${Math.abs(r.shortage) > 5 ? "text-status-cancelled" : ""}`}>{r.shortage.toLocaleString("en-IN")}</td>
              </tr>
            ))}
            {(!data?.rows || data.rows.length === 0) && (<tr><td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">No stock data yet.</td></tr>)}
          </tbody>
          {data?.rows?.length > 0 && (
            <tfoot className="border-t-2 border-primary/40 bg-primary/5 font-bold text-sm">
              <tr>
                <td colSpan={3} className="px-4 py-3 text-right">TOTALS</td>
                <td className="px-4 py-3 text-right font-mono">{data.total_loaded.toLocaleString("en-IN")}</td>
                <td className="px-4 py-3 text-right font-mono">{data.total_delivered.toLocaleString("en-IN")}</td>
                <td colSpan={3} className="px-4 py-3"></td>
                <td className="px-4 py-3 text-right font-mono">{data.total_shortage.toLocaleString("en-IN")}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </CardContent>
    </Card>
  );
}

function SaleReport() {
  const [groupBy, setGroupBy] = useState("bowser");
  const { data } = useQuery({ queryKey: ["sale-report", groupBy], queryFn: () => api.get(`/reports/sale-report?group_by=${groupBy}`).then((r) => r.data) });
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle>Sale report — {groupBy}-wise</CardTitle>
        <Select value={groupBy} onValueChange={setGroupBy}>
          <SelectTrigger className="max-w-44" data-testid="sale-groupby"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="bowser">Group by Bowser</SelectItem>
            <SelectItem value="driver">Group by Driver</SelectItem>
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent className="p-0">
        <table className="data-table w-full">
          <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="px-4 py-2">{groupBy === "bowser" ? "Bowser" : "Driver"}</th>
              <th className="px-4 py-2 text-right">Trips</th>
              <th className="px-4 py-2 text-right">Litres</th>
              <th className="px-4 py-2 text-right">Revenue</th>
              <th className="px-4 py-2 text-right">Avg per trip</th>
            </tr>
          </thead>
          <tbody>
            {(data?.rows || []).map((r) => (
              <tr key={r.id} className="border-b border-border/40 text-sm" data-testid={`sale-row-${r.id}`}>
                <td className="px-4 py-2 font-mono">{r.label}</td>
                <td className="px-4 py-2 text-right font-mono">{r.trips}</td>
                <td className="px-4 py-2 text-right font-mono">{r.litres.toLocaleString("en-IN")}</td>
                <td className="px-4 py-2 text-right font-mono font-semibold">{money(r.revenue)}</td>
                <td className="px-4 py-2 text-right font-mono">{money(r.avg_ticket)}</td>
              </tr>
            ))}
            {(!data?.rows || data.rows.length === 0) && (<tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">No sales data yet.</td></tr>)}
          </tbody>
          {data?.rows?.length > 0 && (
            <tfoot className="border-t-2 border-primary/40 bg-primary/5 font-bold text-sm">
              <tr>
                <td className="px-4 py-3 text-right">TOTAL</td>
                <td className="px-4 py-3 text-right font-mono">{data.total_trips}</td>
                <td className="px-4 py-3 text-right font-mono">{data.total_litres.toLocaleString("en-IN")}</td>
                <td className="px-4 py-3 text-right font-mono">{money(data.total_revenue)}</td>
                <td className="px-4 py-3"></td>
              </tr>
            </tfoot>
          )}
        </table>
      </CardContent>
    </Card>
  );
}

function ShortageReport() {
  const [groupBy, setGroupBy] = useState("bowser");
  const { data } = useQuery({ queryKey: ["shortage-report", groupBy], queryFn: () => api.get(`/reports/shortage-report?group_by=${groupBy}`).then((r) => r.data) });
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle>Shortage — {groupBy}-wise summary</CardTitle>
          <Select value={groupBy} onValueChange={setGroupBy}>
            <SelectTrigger className="max-w-44" data-testid="shortage-groupby"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="bowser">Group by Bowser</SelectItem>
              <SelectItem value="driver">Group by Driver</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent className="p-0">
          <table className="data-table w-full">
            <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-4 py-2">{groupBy === "bowser" ? "Bowser" : "Driver"}</th>
                <th className="px-4 py-2 text-right">Trips</th>
                <th className="px-4 py-2 text-right">Loaded (L)</th>
                <th className="px-4 py-2 text-right">Delivered (L)</th>
                <th className="px-4 py-2 text-right">Shortage</th>
                <th className="px-4 py-2 text-right">%</th>
              </tr>
            </thead>
            <tbody>
              {(data?.summary || []).map((r) => (
                <tr key={r.id} className="border-b border-border/40 text-sm" data-testid={`shortage-row-${r.id}`}>
                  <td className="px-4 py-2 font-mono">{r.label}</td>
                  <td className="px-4 py-2 text-right font-mono">{r.trips}</td>
                  <td className="px-4 py-2 text-right font-mono">{r.loaded.toLocaleString("en-IN")}</td>
                  <td className="px-4 py-2 text-right font-mono">{r.delivered.toLocaleString("en-IN")}</td>
                  <td className={`px-4 py-2 text-right font-mono font-bold ${r.shortage > 5 ? "text-status-cancelled" : r.shortage < 0 ? "text-status-delivered" : ""}`}>{r.shortage.toLocaleString("en-IN")}</td>
                  <td className="px-4 py-2 text-right font-mono">{r.shortage_pct}%</td>
                </tr>
              ))}
              {(!data?.summary || data.summary.length === 0) && (<tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No shortage data yet.</td></tr>)}
            </tbody>
            {data?.summary?.length > 0 && (
              <tfoot className="border-t-2 border-primary/40 bg-primary/5 font-bold text-sm">
                <tr>
                  <td className="px-4 py-3 text-right">TOTAL</td>
                  <td className="px-4 py-3"></td>
                  <td className="px-4 py-3 text-right font-mono">{data.total_loaded.toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3 text-right font-mono">{data.total_delivered.toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3 text-right font-mono">{data.total_shortage.toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3"></td>
                </tr>
              </tfoot>
            )}
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Top shortage trips</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="data-table w-full">
            <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-4 py-2">Order</th>
                <th className="px-4 py-2">Date</th>
                <th className="px-4 py-2">Customer</th>
                <th className="px-4 py-2">Bowser</th>
                <th className="px-4 py-2">Driver</th>
                <th className="px-4 py-2 text-right">Loaded</th>
                <th className="px-4 py-2 text-right">Delivered</th>
                <th className="px-4 py-2 text-right">Shortage</th>
              </tr>
            </thead>
            <tbody>
              {(data?.trips || []).slice(0, 50).map((t) => (
                <tr key={t.order_code} className="border-b border-border/40 text-sm">
                  <td className="px-4 py-2 font-mono">{t.order_code}</td>
                  <td className="px-4 py-2 text-xs">{t.date}</td>
                  <td className="px-4 py-2 text-xs">{t.customer}</td>
                  <td className="px-4 py-2 font-mono text-xs">{t.bowser_code}</td>
                  <td className="px-4 py-2 text-xs">{t.driver_name}</td>
                  <td className="px-4 py-2 text-right font-mono">{t.loaded}</td>
                  <td className="px-4 py-2 text-right font-mono">{t.delivered}</td>
                  <td className={`px-4 py-2 text-right font-mono font-bold ${t.shortage > 5 ? "text-status-cancelled" : ""}`}>{t.shortage}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

